import * as React from 'react';

import {NutAnalyticsClient} from 'nutshell-core/nut-analytics-client';

import {BulletedList} from '../../bulleted-list/bulleted-list';
import {Button} from '../../button/button';
import {colors} from '../../colors/colors';
// @ts-expect-error
import {UpgradeArrowIcon} from '../../icon/svg/upgrade-arrow-icon';
import {Illustration} from '../../illustrations/illustration';
import {TwoPaneModal} from '../../modal/two-pane-modal';
import {Body} from '../../typography/body';
import {ModalHeader} from '../../typography/modal-header';

import {FeatureGateButton} from './feature-gate-button';
import type {FeatureName} from './features-list';
import {getClosingMessage} from './helpers';

import styles from './feature-gate-modal.module.css';

type Props = {
    isOpen?: boolean;
    onClose: () => void;
    title: string;
    description: string;
    bullets: string[];
    isAdmin: boolean;
    feature: FeatureName;
    shouldShowButton?: boolean;
    icon?: React.ReactNode;
    analyticsClient?: NutAnalyticsClient;
    upgradePlanName: string | null | undefined;
};

export function FeatureGateModal(props: Props) {
    const {isAdmin, feature, analyticsClient} = props;
    const bullets = props.bullets.map((bullet) => <Body key={bullet}>{bullet}</Body>);
    const closingMessage = getClosingMessage(feature, props.upgradePlanName);
    // for sms gates, we don't want to direct user to billing page if the
    // company already has licenses, regardless of if they have access to
    // billing.
    const shouldShowButton =
        typeof props.shouldShowButton === 'boolean' ? props.shouldShowButton : isAdmin;

    React.useEffect(() => {
        if (analyticsClient && feature && props.isOpen) {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_FEATURE_GATE_VIEWED, {
                feature,
                isAdmin,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen]);

    return (
        <TwoPaneModal
            // @ts-expect-error
            isOpen={props.isOpen}
            maxHeight={600}
            onClose={props.onClose}
            closeButton={!props.isAdmin}
            leftSection={
                <div className={styles.left}>
                    {props.icon || <Illustration src='bank-safe' width={260} />}
                </div>
            }
            rightSection={
                <div className={styles.container}>
                    <ModalHeader>
                        <div className='flex gap-8 align-center'>
                            <UpgradeArrowIcon size={20} wrapWithDiv={false} color={colors.blue} />
                            <Body size='large' isBold={true}>
                                {props.title}
                            </Body>
                        </div>
                    </ModalHeader>
                    <div className='mb-16'>
                        <Body>{props.description}</Body>
                    </div>
                    <BulletedList bullets={bullets} />
                    <Body>{closingMessage}</Body>
                    {shouldShowButton && (
                        <div className={styles.footer}>
                            <Button
                                variant='text-secondary'
                                size='normal'
                                onClick={props.onClose}
                                noPadding={true}
                            >
                                Not right now
                            </Button>
                            <FeatureGateButton onClick={props.onClose} />
                        </div>
                    )}
                </div>
            }
        />
    );
}
